import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';

import type { SkuDTO, SkuType } from '@invenco/products-interface';

import {
  CollapsibleCard,
  CollapsibleCardContent,
  CollapsibleCardTrigger,
} from '@/components/collapsible-card';
import { Badge } from '@/components/core/badge';
import { Card, CardAction, CardHeader, CardTitle } from '@/components/core/card';
import { DataTable, useDataTable, useDataTableState } from '@/components/data-table';
import { skuTypeTitle } from '@/shared/title-maps';

import { SkuBadge } from '../../components/sku-badge';

type Props = {
  sku?: SkuDTO;
  isLoading?: boolean;
};

type AssemblyRow = {
  id: string;
  skuName: string;
  skuDescription?: string | null;
  skuType: SkuType;
};

const columnHelper = createColumnHelper<AssemblyRow>();

const columns = [
  columnHelper.accessor('skuName', {
    header: 'SKU / Description',
    cell: ({ getValue, row }) => (
      <div className="flex min-w-0 items-center gap-2">
        <SkuBadge name={getValue()} type={row.original.skuType} />
        {row.original.skuDescription ? (
          <span className="min-w-0 truncate text-sm">{row.original.skuDescription}</span>
        ) : null}
      </div>
    ),
    meta: { truncate: true },
  }),
  columnHelper.accessor('skuType', {
    header: 'Type',
    cell: ({ getValue }) => <Badge variant="secondary">{skuTypeTitle[getValue()]}</Badge>,
  }),
];

export function AssembliesCard({ sku, isLoading }: Props) {
  const rows = useMemo<AssemblyRow[]>(
    () =>
      sku?.assemblies?.map(({ assemblySku }) => ({
        id: assemblySku.id,
        skuName: assemblySku.name,
        skuDescription: assemblySku.description,
        skuType: assemblySku.type,
      })) ?? [],
    [sku?.assemblies],
  );

  const hasRows = rows.length > 0;

  const { state, onSortingChange } = useDataTableState({
    columns,
    tableId: 'sku-assemblies-table',
    defaultPageSize: 10,
  });

  const table = useDataTable({
    data: rows,
    columns,
    state,
    isLoading,
    manualPagination: true,
    manualSorting: false,
    onSortingChange,
  });

  const titleText = hasRows ? 'Used as component' : 'Not used as component';

  if (!hasRows) {
    return (
      <Card aria-label={titleText}>
        <CardHeader>
          <CardTitle>{titleText}</CardTitle>
        </CardHeader>
      </Card>
    );
  }

  return (
    <CollapsibleCard aria-label={titleText}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span>{titleText}</span>
          <Badge variant="secondary" aria-label={`with ${table.getRowCount()} rows`}>
            {table.getRowCount()}
          </Badge>
        </CardTitle>
        <CardAction>
          <CollapsibleCardTrigger aria-label={titleText} />
        </CardAction>
      </CardHeader>
      <CollapsibleCardContent>
        <DataTable table={table} />
      </CollapsibleCardContent>
    </CollapsibleCard>
  );
}
